import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/axios';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import DateInput from '../../components/ui/DateInput';
import { Card, CardHeader, CardContent, CardFooter } from '../../components/ui/Card';
import { Plus, Edit, Trash2, Loader2, FileText, Users, LogOut, Moon, Sun, ShieldAlert } from 'lucide-react';

const Clients = () => {
  const navigate = useNavigate();
  const { isDark, toggleTheme, theme } = useTheme();
  const { logout } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [currentClient, setCurrentClient] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    date_of_birth: '',
    email: '',
    cellphone: '',
    client_code: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await api.get('/clients');
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.full_name.trim()) newErrors.full_name = 'Name is required';
    // Date of birth is optional for advisor-created clients
    if (formData.date_of_birth) {
      const birthDate = new Date(formData.date_of_birth);
      const today = new Date();
      let calcAge = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) calcAge--;
      if (calcAge < 18 || calcAge > 100) newErrors.date_of_birth = 'Client must be between 18 and 100 years old';
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.cellphone.trim()) newErrors.cellphone = 'Cellphone is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveClient = async () => {
    if (!validateForm()) return;

    // Clean up data
    const payload = {
      ...formData,
      date_of_birth: formData.date_of_birth,
      client_code: formData.client_code.trim() || null,
    };

    try {
      if (currentClient) {
        await api.patch(`/clients/${currentClient.id}`, payload);
      } else {
        await api.post('/clients', payload);
      }
      setModalOpen(false);
      setErrors({});
      fetchClients();
    } catch (error) {
      console.error('Failed to save client:', error);
      const msg = error.response?.data?.error
        || error.response?.data?.message
        || error.response?.data?.details
        || error.message
        || 'Failed to save client';
      setErrors({ submit: msg });
    }
  };

  const handleEditClient = (client) => {
    setCurrentClient(client);
    setFormData({
      full_name: client.full_name,
      date_of_birth: client.date_of_birth ? new Date(client.date_of_birth).toISOString().split('T')[0] : '',
      email: client.email,
      cellphone: client.cellphone,
      client_code: client.client_code || '',
    });
    setErrors({});
    setModalOpen(true);
  };

  const handleNewClient = () => {
    setCurrentClient(null);
    setFormData({ full_name: '', date_of_birth: '', email: '', cellphone: '', client_code: '' });
    setErrors({});
    setModalOpen(true);
  };

  const handleDeleteClient = async (id) => {
    const client = clients.find(c => c.id === id);
    setClientToDelete(client);
    setDeleteReason('');
    setDeleteModalOpen(true);
  };

  const confirmDeleteClient = async () => {
    if (!deleteReason.trim()) {
      alert('Please provide a reason for deletion.');
      return;
    }
    try {
      await api.delete(`/clients/${clientToDelete.id}`, { data: { reason: deleteReason } });
      setDeleteModalOpen(false);
      setClientToDelete(null);
      setDeleteReason('');
      fetchClients();
    } catch (error) {
      console.error('Failed to delete client:', error);
      alert('Failed to delete client: ' + (error.response?.data?.error || error.message));
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.surface }}>
      <header className="border-b" style={{ backgroundColor: theme.card, borderColor: theme.border }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold" style={{ color: theme.text }}>RDA Investment Dashboard</h1>
              <p className="text-sm" style={{ color: theme.textSecondary }}>Manage Clients</p>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={toggleTheme} className="p-2 rounded-lg" style={{ backgroundColor: theme.surface, border: `1px solid ${theme.border}` }}>
                {isDark ? <Sun className="w-5 h-5" style={{ color: theme.text }} /> : <Moon className="w-5 h-5" />}
              </button>
              <Button variant="ghost" onClick={() => navigate('/dashboard')}>
                <FileText className="w-4 h-4 mr-2" /> Dashboard
              </Button>
              <Button variant="ghost" onClick={() => { logout(); navigate('/login'); }}>
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold" style={{ color: theme.text }}>Clients</h2>
          <Button onClick={handleNewClient} className="whitespace-nowrap">
            <Plus className="w-5 h-5 mr-2" /> Add Client
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <Loader2 className="w-16 h-16 mx-auto mb-4 animate-spin" style={{ color: theme.primary }} />
            <p className="text-lg" style={{ color: theme.textSecondary }}>Loading clients...</p>
          </div>
        ) : clients.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Users className="w-16 h-16 mx-auto mb-4 opacity-20" style={{ color: theme.text }} />
              <p className="text-lg" style={{ color: theme.textSecondary }}>No clients yet</p>
              <p className="text-sm mt-2" style={{ color: theme.textSecondary }}>
                Click the 'Add Client' button to get started
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold" style={{ color: theme.text }}>Client List</h3>
                <div className="relative w-full max-w-sm">
                 {/* Search bar can be added here */}
                </div>
              </div>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="min-w-full divide-y" style={{ borderColor: theme.border }}>
                <thead>
                  <tr>
                    <th scope="col" className="py-3 px-4 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.textSecondary }}>Name</th>
                    <th scope="col" className="py-3 px-4 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.textSecondary }}>Email</th>
                    <th scope="col" className="py-3 px-4 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.textSecondary }}>Cellphone</th>
                    <th scope="col" className="py-3 px-4 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.textSecondary }}>Client Code</th>
                    <th scope="col" className="py-3 px-4 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.textSecondary }}>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: theme.border }}>
                  {clients.map((client) => (
                    <tr key={client.id}>
                      <td className="py-3 px-4 whitespace-nowrap text-sm" style={{ color: theme.text }}>{client.full_name}</td>
                      <td className="py-3 px-4 whitespace-nowrap text-sm" style={{ color: theme.textSecondary }}>{client.email}</td>
                      <td className="py-3 px-4 whitespace-nowrap text-sm" style={{ color: theme.textSecondary }}>{client.cellphone}</td>
                      <td className="py-3 px-4 whitespace-nowrap text-sm" style={{ color: theme.text }}>{client.client_code || '-'}</td>
                      <td className="py-3 px-4 whitespace-nowrap text-sm space-x-2">
                        <button onClick={() => handleEditClient(client)} className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
                          <Edit className="w-4 h-4 inline-block" />
                        </button>
                        <button onClick={() => handleDeleteClient(client.id)} className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300">
                          <Trash2 className="w-4 h-4 inline-block" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Modal for Add/Edit Client */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="max-w-lg w-full">
            <Card>
              <CardHeader>
                <h2 className="text-2xl font-bold" style={{ color: theme.text }}>{currentClient ? 'Edit Client' : 'Add New Client'}</h2>
              </CardHeader>
              <CardContent>
                <form onSubmit={(e) => { e.preventDefault(); handleSaveClient(); }} noValidate className="space-y-4">
                  {errors.submit && <p className="text-sm text-red-600 dark:text-red-400">{errors.submit}</p>}

                  <Input label="Full Name" value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} error={errors.full_name} required />
                  <div className="grid grid-cols-2 gap-4">
                    <DateInput label="Date of Birth (optional)" value={formData.date_of_birth} onChange={(iso) => setFormData({ ...formData, date_of_birth: iso })} error={errors.date_of_birth} />
                    <Input label="Client Code" value={formData.client_code} onChange={(e) => setFormData({ ...formData, client_code: e.target.value })} />
                  </div>
                  <Input label="Email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} error={errors.email} required />
                  <Input label="Cellphone" value={formData.cellphone} onChange={(e) => setFormData({ ...formData, cellphone: e.target.value })} error={errors.cellphone} required />

                  <div className="pt-4 flex justify-end space-x-3">
                    <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
                    <Button type="submit">Save Client</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="max-w-lg w-full">
            <Card>
              <CardHeader>
                <h2 className="text-2xl font-bold flex items-center gap-2" style={{ color: theme.text }}>
                  <ShieldAlert className="w-6 h-6 text-red-500" />
                  Delete Client
                </h2>
                <p className="text-sm" style={{ color: theme.textSecondary }}>
                  You are about to delete <strong>{clientToDelete?.full_name}</strong> and all associated data.
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: theme.text }}>Reason for deletion</label>
                    <textarea
                      className="w-full px-3 py-2 rounded-lg border resize-none"
                      style={{
                        backgroundColor: theme.surface,
                        borderColor: theme.border,
                        color: theme.text,
                        minHeight: '100px'
                      }}
                      placeholder="Please provide a reason for deleting this client..."
                      value={deleteReason}
                      onChange={(e) => setDeleteReason(e.target.value)}
                    />
                  </div>
                  <div className="p-3 rounded-lg" style={{ backgroundColor: '#fef2f2', border: '1px solid #fca5a5' }}>
                    <p className="text-sm text-red-800 dark:text-red-200">
                      <strong>Warning:</strong> This action will permanently delete the client and all their assessments, answers, and recommendations. This cannot be undone.
                    </p>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-end space-x-3">
                <Button type="button" variant="secondary" onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
                <Button onClick={confirmDeleteClient} className="bg-red-600 hover:bg-red-700 text-white">
                  <Trash2 className="w-4 h-4 mr-2" /> Delete Client
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;
